


import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { validateUserSession } from '../helpers/validateUserSession.js';

import useUIPresentationMode from '../helpers/useUIPresentationMode.js';

import MapShell from '../MapShell.js';

import Map from './MapComponent';
import SideBarLeft from './SideBarLeft.js';
import SideBarRight from './SideBarRight.js';

//import ShowLeftSidebarIcon from '../images/ShowLeftSideBarButtonLight@3x.png';
//import ShowLeftSidebarIconHover from '../images/ShowLeftSideBarButtonBlue@3x.png';
//import ShowRightSidebarIcon from '../images/ShowRightSideBarButtonLight@3x.png';
//import ShowRightSidebarIconHover from '../images/ShowRightSideBarButtonBlue@3x.png';
//import RestoreSidebarButton from './helpers/RestoreSideBarButton.js';

import { fetchMapLayers } from '../helpers/fetchMapLayers.js';
import { initialiseLayers } from '../helpers/initialiseLayers.js';
import { fetchPlanData } from '../helpers/fetchPlanData.js';

import { fetchPlanOverlayImageUrl } from '../helpers/fetchPlanOverlayImageUrl.js';
import { generateMapSnapshot } from '../helpers/generateMapSnapshot.js';
import { publishCurrentUrlToParent, replaceUrlState } from '../helpers/urlUpdaterHelper.js';
const snapshotQuality = 'HD'; // options: 'SD' or 'HD'


/*-----------------------------------------------------------------------------*/


const MainLayout = ({ onLogout }) => {

  const navigate = useNavigate();

  const [sessionChecked, setSessionChecked] = useState(false);  // This gatekeeper makes sure we have a valid logged in user before progressing.

  const { presentationModeSize, presentationModeiFrame } = useUIPresentationMode();

  const mapRef = useRef(null);
  const topLayerRef = useRef(null);
  const mapComponentRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);

  const [selectedBaseMap, setSelectedBaseMap] = useState('None');
  const [layers, setLayers] = useState({ primary: null, secondary: [] });

  const [selectedLandInfo, setSelectedLandInfo] = useState(null);


  const handleBaseMapChange = (newBaseMap) => {

    setSelectedBaseMap(newBaseMap);

    if (mapComponentRef.current?.changeBaseMap) {

      mapComponentRef.current.changeBaseMap(newBaseMap); // <-- triggers actual map layer change
    }
    else {
      console.warn('mapComponentRef.current.changeBaseMap is not defined');
    }
  };


  const [isParcelBoundariesVisible, setIsParcelBoundariesVisible] = useState(false);


  const toggleParcelBoundaries = (visible) => {
    if (mapComponentRef.current?.toggleParcelBoundaries) {
      mapComponentRef.current.toggleParcelBoundaries(visible);
    } else {
      console.warn('toggleParcelBoundaries not ready yet');
    }
  };


  const [plansBySeries, setPlansBySeries] = useState({});
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [activeOverlayPlanId, setActiveOverlayPlanId] = useState(null);

  const overlayCentreRef = useRef(null);
  const [okayToUpdateURLInt, setOkayToUpdateURLInt] = useState(false);

  const urlParams = new URLSearchParams(window.location.search);
  const initialPlanId = urlParams.get('p');
  const dontWaitToDropInt = urlParams.get('dwtd');


  /*-----------------------------------------------------------------------------*/


  // Kick the user back out to the welcome screen if there is an issue with the
  // session token meaning there is no valid user session.
  useEffect(() => {
    validateUserSession(() => navigate('/')).then((isValid) => {
      if (isValid) {
        setSessionChecked(true);
        publishCurrentUrlToParent();
      }
    });
  }, [navigate]);


  useEffect(() => {

    if (!mapReady || !sessionChecked || !mapComponentRef.current) return;

    const runInitialisation = async () => {
      await initialiseLayers({
        fetchMapLayers,
        mapRef,
        topLayerRef,
        setLayers,
        setSelectedBaseMap,
        setIsParcelBoundariesVisible,
        changeBaseMap: mapComponentRef.current?.changeBaseMap
      });
    };

    runInitialisation();

    //console.log('[layers-init] effect fired', { mapReady, sessionChecked });

  }, [mapReady, sessionChecked]);


  // Monitor for the save map screenshot keystroke. shift+cmnd+s on Mac and shift+ctrl+s on Windows.
  useEffect(() => {

    const handleKeydown = async (e) => {

      if (e.metaKey && e.shiftKey && e.key.toLowerCase() === 'x') {

        console.log('[Snapshot] Triggering snapshot generation...');

        const urlParams = new URLSearchParams(window.location.search);
        const planId = urlParams.get('p');

        let overlayImageUrl = null;
        let overlayX = null;
        let overlayY = null;

        const overlayData = await fetchPlanOverlayImageUrl(planId);

        if (!overlayData || !overlayData.url) {
          console.log('[Snapshot] No overlay image available — continuing without drawing a plan overlay.');
        } else {
          overlayImageUrl = overlayData.url;
          overlayX = overlayData.overlayX || null;
          overlayY = overlayData.overlayY || null;
        }

        let adjustedZoom = parseInt(urlParams.get('z'), 10);
        let boundaryScale = 1; // normal

        if (snapshotQuality === 'HD') {
          adjustedZoom += 1; // bump zoom
          boundaryScale = 2; // scale up drawing elements
        }

        generateMapSnapshot({
          centerLat: parseFloat(urlParams.get('lat')),
          centerLng: parseFloat(urlParams.get('lng')),
          zoom: adjustedZoom,
          landLat: parseFloat(urlParams.get('lilat')),
          landLng: parseFloat(urlParams.get('lilng')),
          polygonCoords: window.currentParcelPolygon || null,
          overlayImageUrl,
          overlayCenterLat: parseFloat(urlParams.get('olat')),
          overlayCenterLng: parseFloat(urlParams.get('olng')),
          overlayRotationDeg: parseFloat(urlParams.get('r') || '0'),
          mirrorOverlay: urlParams.get('mr') === '1',
          overlayX,
          overlayY,
          boundaryScale
        });
      }
    };

    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, []);


  // Load the plans.
  useEffect(() => {

    const loadPlans = async () => {

      const overlayLatParam = parseFloat(urlParams.get('olat'));
      const overlayLngParam = parseFloat(urlParams.get('olng'));
      const hasOverlayCoords = !isNaN(overlayLatParam) && !isNaN(overlayLngParam);

      const groupedPlans = await fetchPlanData();
      setPlansBySeries(groupedPlans);

      if (initialPlanId) {

        const allPlans = Object.values(groupedPlans).flat();  // Flatten all plans into one list to search
        const match = allPlans.find(plan => plan.id === initialPlanId);

        if (match) {

          setActiveOverlayPlanId(match.id);  // Only set the "active" plan. We wait to set the "selected" plan as we may not want to place that immediately.

          if (dontWaitToDropInt === '1') {

            setSelectedPlanId(match.id);  // Also load overlay on the map if desired.

            if (hasOverlayCoords && mapRef.current) {

              const overlayCenter = { lat: overlayLatParam, lng: overlayLngParam };
              overlayCentreRef.current = overlayCenter;
            }
          }
        }
        else {
          //console.log('No match to URL');
        }

        setOkayToUpdateURLInt(true); // All the inits are done so it's safe to begin writing back to the URL.
      }
    };

    loadPlans();
    // Disable the eslint warning about the dependency array as we only want this to run once.
    // It was causing an infinite loop and making the overlay jump all over the place.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // Clear selected land info when the user clicks the button in the right sidebar.
  const clearSelectedLand = () => {
    setSelectedLandInfo(null);
    mapComponentRef.current?.clearLandOverlay?.();

    const url = new URL(window.location);
    url.searchParams.delete('lilat');
    url.searchParams.delete('lilng');
    replaceUrlState(url);
  };


  // Clear selected plan info when the user clicks the button in the left sidebar. Overlay should
  // disappear but plan is still active, so the drop plan button reappears and plan info stays in
  // the right side bar.
  const clearOverlayOnly = () => {
    setSelectedPlanId(null);
  };


  // Clear selected plan and active plan when the user clicks the button in the right sidebar.
  // Overlay should disappear and plan is no longer active, so no drop plan button and plan info
  // gone from right side bar.
  const clearSelectedPlanAndOverlay = () => {
    setSelectedPlanId(null);
    setActiveOverlayPlanId(null);
  };
  

  return (
    <MapShell
      presentationModeSize={presentationModeSize}
      leftContent={
        <SideBarLeft
          isVisible={true}                 // MapShell will handle collapse; this just seeds it as visible
          onRequestClose={() => { }}        // MapShell injects the real handler
          onLogout={onLogout}
          layers={layers}
          selectedBaseMap={selectedBaseMap}
          setSelectedBaseMap={setSelectedBaseMap}
          onBaseMapChange={handleBaseMapChange}
          isParcelBoundariesVisible={isParcelBoundariesVisible}
          setIsParcelBoundariesVisible={setIsParcelBoundariesVisible}
          toggleParcelBoundaries={toggleParcelBoundaries}
          plansBySeries={plansBySeries}
          selectedPlanId={selectedPlanId}
          activeOverlayPlanId={activeOverlayPlanId}
          onPlanSelect={(plan) => {
            if (mapComponentRef.current?.markPlanSelectionUserInitiated) {
              mapComponentRef.current.markPlanSelectionUserInitiated();
            }
            setSelectedPlanId(plan.id);
            setActiveOverlayPlanId(plan.id);
          }}
          clearOverlayOnly={clearOverlayOnly}
        />
      }
      mapContent={
        <Map
          presentationModeSize={presentationModeSize}
          presentationModeiFrame={presentationModeiFrame}
          ref={mapComponentRef}
          mapRef={mapRef}
          onMapLoaded={() => setMapReady(true)}
          topLayerRef={topLayerRef}
          selectedPlanId={selectedPlanId}
          activeOverlayPlanId={activeOverlayPlanId}
          overlayCentreRef={overlayCentreRef}
          selectedBaseMap={selectedBaseMap}
          setSelectedBaseMap={setSelectedBaseMap}
          isParcelBoundariesVisible={isParcelBoundariesVisible}
          setIsParcelBoundariesVisible={setIsParcelBoundariesVisible}
          layers={layers}
          setLayers={setLayers}
          plansBySeries={plansBySeries}
          setPlansBySeries={setPlansBySeries}
          setSelectedPlanId={setSelectedPlanId}
          okayToUpdateURLInt={okayToUpdateURLInt}
          setOkayToUpdateURLInt={setOkayToUpdateURLInt}
          onLandSelect={(landInfo) => setSelectedLandInfo(landInfo)}
        />
      }
      rightContent={
        <SideBarRight
          isExpanded={true}                 // MapShell manages visibility/collapse
          onRequestClose={() => { }}        // MapShell injects the real handler
          selectedPlanId={selectedPlanId}
          activeOverlayPlanId={activeOverlayPlanId}
          selectedLandInfo={selectedLandInfo}
          onRequestClearLand={clearSelectedLand}
          onRequestClearPlan={clearSelectedPlanAndOverlay}
        />
      }
    />
  );

};


export default MainLayout;


